import { useCallback, useEffect, useRef, useState } from "react";
import { addDays, differenceInDays, format, subDays } from "date-fns";
import { useSelector } from "react-redux";

import { useTour } from "hooks/useTour";

import { fetchBillsPage } from "features/boletos/services/boletosApi";
import type { BillApiRow, BillListItem, SelectedUnitSlice } from "features/boletos/types";
import { mapBillsForList } from "features/boletos/utils/mapBillsForList";

function applyPeriodFromUrlSearch(setStatus: (s: string) => void, setSelectedPeriod: (s: string) => void): void {
  if (typeof window === "undefined") return;

  const { search } = window.location;
  const params = new URLSearchParams(search);

  const statusParams = params.get("status");
  const beginDate = params.get("begin-date");
  const endDate = params.get("end-date");
  const selectedPeriodParams = params.get("selected-period");

  if (statusParams) {
    setStatus(statusParams);
  }

  if (selectedPeriodParams) {
    setSelectedPeriod(selectedPeriodParams);
  }

  if (beginDate && endDate && !selectedPeriodParams) {
    const beginFns = addDays(new Date(beginDate), 1);
    const endFns = addDays(new Date(endDate), 1);
    const daysDifference = differenceInDays(endFns, beginFns);

    switch (daysDifference) {
      case 90:
        setSelectedPeriod("old90days");
        break;
      case 60:
        setSelectedPeriod("old60days");
        break;
      case 30:
        setSelectedPeriod("old30days");
        break;
      case 15:
        setSelectedPeriod("old15days");
        break;
      default:
        break;
    }
  }
}

export function useBoletosPage(): {
  bills: BillListItem[];
  isLoading: boolean;
  isFilter: boolean;
  isShowModalContent: boolean;
  setIsShowModalContent: (v: boolean) => void;
  isShowModalFilter: boolean;
  setIsShowModalFilter: (v: boolean) => void;
  isShowModalNothingContained: boolean;
  setIsShowModalNothingContained: (v: boolean) => void;
  isShowModalAutomaticDebit: boolean;
  setIsShowModalAutomaticDebit: (v: boolean) => void;
  modalPayment: BillApiRow | null;
  modalInstallments: BillApiRow | null;
  status: string;
  selectedPeriod: string;
  totalPages: number;
  selectedPage: number;
  setSelectedPage: (p: number) => void;
  handleSelectedPeriod: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  handleStatus: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  handleShow: (payment: BillApiRow, installments: BillApiRow) => void;
  showTour: boolean;
  tourStep: number;
  tourType: string;
  clearFiltersHref: () => void;
} {
  const [bills, setBills] = useState<BillListItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isFilter, setIsFilter] = useState(false);
  const [isShowModalContent, setIsShowModalContent] = useState(false);
  const [isShowModalFilter, setIsShowModalFilter] = useState(false);
  const [isShowModalNothingContained, setIsShowModalNothingContained] = useState(false);
  const [isShowModalAutomaticDebit, setIsShowModalAutomaticDebit] = useState(false);
  const [modalPayment, setModalPayment] = useState<BillApiRow | null>(null);
  const [modalInstallments, setModalInstallments] = useState<BillApiRow | null>(null);
  const [status, setStatus] = useState("");
  const [selectedPeriod, setSelectedPeriod] = useState("recentToOld");
  const [totalPages, setTotalPages] = useState(0);
  const [selectedPage, setSelectedPage] = useState(1);

  const statusRef = useRef(status);
  statusRef.current = status;

  const { selectedUnit } = useSelector((state: { selectUnitReducer: { selectedUnit: SelectedUnitSlice } }) => ({
    selectedUnit: state.selectUnitReducer.selectedUnit,
  }));

  const { showTour, setTourType, setSteps, tourStep, tourType } = useTour();

  const loadBills = useCallback(async (page: number): Promise<void> => {
    try {
      const { search } = window.location;
      const getParams = new URLSearchParams(search);
      const beginDate = getParams.get("begin-date");
      const endDate = getParams.get("end-date");
      const filterStatus = getParams.get("status") || statusRef.current;
      const order = getParams.get("order");
      const invoiceNumber = getParams.get("invoice-number");

      if (!!beginDate || !!endDate || !!filterStatus || !!invoiceNumber) {
        setIsFilter(true);
      }

      const { data, totalPages: pages } = await fetchBillsPage({
        page,
        unitInstanceId: selectedUnit?.instanceId,
        beginDate,
        endDate,
        filterStatus,
        order,
        invoiceNumber,
      });

      if (!data) {
        setIsLoading(false);
      }

      setTotalPages(pages);

      const { bills: nextBills, overdueCount } = mapBillsForList(
        data,
        selectedUnit?.hasDirectDebit
      );
      setBills(nextBills);

      if (overdueCount > 0) {
        setIsShowModalAutomaticDebit(true);
      }
    } catch {
      setIsLoading(false);
    }
  }, [selectedUnit?.hasDirectDebit, selectedUnit?.instanceId]);

  useEffect(() => {
    applyPeriodFromUrlSearch(setStatus, setSelectedPeriod);

    void (async () => {
      await loadBills(selectedPage);
      setIsLoading(false);
    })();
    // Comportamento legado: refetch ao mudar página ou total de páginas retornado pela API.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- paridade com efeito anterior
  }, [selectedPage, totalPages, loadBills]);

  useEffect(() => {
    setTourType("consulta-debitos");
    setSteps(3);
  }, [showTour, setTourType, setSteps]);

  const handleSelectedPeriod = (e: React.ChangeEvent<HTMLSelectElement>): void => {
    const { value } = e.currentTarget;
    const statusQuery = status ? `&status=${status}` : "";

    if (value === "old15days") {
      const beginDate = format(subDays(new Date(), 15), "yyyy-MM-dd");
      const endDate = format(new Date(), "yyyy-MM-dd");
      window.location.href = `/consulta-debitos?begin-date=${beginDate}&end-date=${endDate}${statusQuery}&selected-period=${value}`;
    } else if (value === "old30days") {
      const beginDate = format(subDays(new Date(), 30), "yyyy-MM-dd");
      const endDate = format(new Date(), "yyyy-MM-dd");
      window.location.href = `/consulta-debitos?begin-date=${beginDate}&end-date=${endDate}${statusQuery}&selected-period=${value}`;
    } else if (value === "old60days") {
      const beginDate = format(subDays(new Date(), 60), "yyyy-MM-dd");
      const endDate = format(new Date(), "yyyy-MM-dd");
      window.location.href = `/consulta-debitos?begin-date=${beginDate}&end-date=${endDate}${statusQuery}&selected-period=${value}`;
    } else if (value === "old90days") {
      const beginDate = format(subDays(new Date(), 90), "yyyy-MM-dd");
      const endDate = format(new Date(), "yyyy-MM-dd");
      window.location.href = `/consulta-debitos?begin-date=${beginDate}&end-date=${endDate}${statusQuery}&selected-period=${value}`;
    } else if (value === "recentToOld") {
      window.location.href = `/consulta-debitos?selected-period=recentToOld${statusQuery}`;
    } else if (value === "oldToRecent") {
      window.location.href = `/consulta-debitos?order=dueDate${statusQuery}&selected-period=oldToRecent`;
    }
  };

  const handleStatus = (e: React.ChangeEvent<HTMLSelectElement>): void => {
    const { value } = e.currentTarget;
    const { search } = window.location;
    const params = new URLSearchParams(search);
    const statusParams = params.get("status");

    if (statusParams) {
      window.location.href = search.replace(statusParams, value);
      return;
    }

    if (search) {
      window.location.href = `${search}&status=${value}`;
      return;
    }

    window.location.href = `?status=${value}`;
  };

  const handleShow = (payment: BillApiRow, installments: BillApiRow): void => {
    setModalPayment(payment);
    setModalInstallments(installments);
    setIsShowModalContent(true);
  };

  const clearFiltersHref = (): void => {
    window.location.href = "/consulta-debitos";
  };

  return {
    bills,
    isLoading,
    isFilter,
    isShowModalContent,
    setIsShowModalContent,
    isShowModalFilter,
    setIsShowModalFilter,
    isShowModalNothingContained,
    setIsShowModalNothingContained,
    isShowModalAutomaticDebit,
    setIsShowModalAutomaticDebit,
    modalPayment,
    modalInstallments,
    status,
    selectedPeriod,
    totalPages,
    selectedPage,
    setSelectedPage,
    handleSelectedPeriod,
    handleStatus,
    handleShow,
    showTour,
    tourStep,
    tourType,
    clearFiltersHref,
  };
}
